import { useEffect, useState } from "react";
import { FileText, Download, Eye, Loader2, ChevronRight } from "lucide-react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    reportRiskApi,
    reportRiskDownloadApi,
    getRiskHistorySnapshotApi,
} from "../../../services/axios/AuthApis/RisksManagementApis";
import { useSelector } from "react-redux";
import ApiLoader from "../../ui/ApiLoader";
import { DataTable } from "../../common/DataTable";

const REPORTS = [
    {
        id: "risk-register",
        name: "Risk Register",
        description: "Complete list of all identified risks with their current status",
        icon: FileText,
        color: "blue"
    },
    {
        id: "treatment",
        name: "Treatment Report",
        description: "All treatment plans and their implementation status",
        icon: FileText,
        color: "green"
    },
    {
        id: "executive-summary",
        name: "Executive Summary",
        description: "High-level overview with key metrics",
        icon: FileText,
        color: "purple"
    }
];

export const Reports = ({ userId, userRole }) => {
    const [loading, setLoading] = useState({});
    const [previewData, setPreviewData] = useState(null);
    const [previewReport, setPreviewReport] = useState(null);
    const selectedCompany = useSelector((state) => state.company.selectedCompany);

    const [searchTerm, setSearchTerm] = useState("");
    const [riskStatus, setRiskStatus] = useState("");
    const [riskSeverity, setRiskSeverity] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyRows, setHistoryRows] = useState([]);
    const [historyDetail, setHistoryDetail] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    const customerId = (userRole === "security_analyst" || userRole === "auditor") && selectedCompany
        ? (selectedCompany?.user_id || userId)
        : userId;

    const totalPages = Math.max(1, Math.ceil(totalCount / itemsPerPage));

    const loadRiskHistory = async (page = 1) => {
        if (!customerId) {
            return;
        }

        setHistoryLoading(true);
        try {
            const res = await getRiskHistorySnapshotApi({
                userId: customerId,
                fromDate,
                toDate,
                search: searchTerm,
                status: riskStatus,
                severity: riskSeverity,
                page,
                limit: itemsPerPage,
            });

            if (res.success) {
                setHistoryRows(res.data || []);
                setTotalCount(res.total_count || 0);
                setCurrentPage(res.current_page || page);
            }
        } catch (err) {
            console.error("Failed to load risk history", err);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        if (userRole === "auditor") {
            loadRiskHistory(1);
        }
    }, [userRole, customerId, itemsPerPage]);

    const generateReport = async (reportId, action = "preview") => {
        setLoading(prev => ({ ...prev, [reportId]: true }));
        try {
            if (action === "preview") {
                const res = await reportRiskApi(reportId, customerId, action, userRole);
                if (res.success) {
                    setPreviewData(res);
                    setPreviewReport(REPORTS.find(r => r.id === reportId));
                }
            } else {
                const { blob, filename } = await reportRiskDownloadApi(reportId, customerId, action, userRole, "csv");
                const url = URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = filename;
                a.click();
                URL.revokeObjectURL(url);
            }
        } catch (err) {
            console.error("Failed to generate report", err);
        } finally {
            setLoading(prev => ({ ...prev, [reportId]: false }));
        }
    };

    const historyColumns = [
        {
            accessorKey: "risk_id",
            header: "Risk ID",
            cell: ({ row }) => <span className="font-mono text-red-600">{row.original.risk_id || "N/A"}</span>,
        },
        {
            accessorKey: "risk_description",
            header: "Description",
            cell: ({ row }) => <span>{row.original.risk_description || "N/A"}</span>,
        },
        {
            accessorKey: "category",
            header: "Category",
            cell: ({ row }) => <span>{row.original.category || "N/A"}</span>,
        },
        {
            accessorKey: "risk_response",
            header: "Response",
            cell: ({ row }) => <span className="capitalize">{row.original.risk_response || "N/A"}</span>,
        },
        {
            accessorKey: "inherent_risk_score",
            header: "Inherent",
            cell: ({ row }) => <span>{row.original.inherent_risk_score || "N/A"}</span>,
        },
        {
            accessorKey: "residual_risk_score",
            header: "Residual",
            cell: ({ row }) => <span>{row.original.residual_risk_score || "N/A"}</span>,
        },
        {
            id: "history",
            header: "History",
            enableSorting: false,
            cell: ({ row }) => (
                <button
                    onClick={() => setHistoryDetail(row.original)}
                    className="flex items-center gap-1 text-slate-600 hover:text-slate-900"
                >
                    {row.original.history_count || 0} <ChevronRight className="w-4 h-4" />
                </button>
            ),
        },
    ];

    const clearFilters = () => {
        setSearchTerm("");
        setRiskStatus("");
        setRiskSeverity("");
        setFromDate("");
        setToDate("");
        setCurrentPage(1);
        loadRiskHistory(1);
    };

    const colorClasses = {
        red: "bg-red-50 border-red-200 text-red-600",
        blue: "bg-blue-50 border-blue-200 text-blue-600",
        green: "bg-green-50 border-green-200 text-green-600",
        purple: "bg-purple-50 border-purple-200 text-purple-600",
    };

    return (
        <div className="space-y-6" data-testid="reports-tab">
            {userRole !== "auditor" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {REPORTS.map(report => {
                        const Icon = report.icon;
                        return (
                            <Card key={report.id} className="card-surface" data-testid={`report-${report.id}`}>
                                <CardContent className="p-4">
                                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-4 border ${colorClasses[report.color]}`}>
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="font-semibold text-slate-800 mb-2">{report.name}</h3>
                                    <p className="text-sm text-slate-500 mb-4">{report.description}</p>

                                    <div className="flex items-center gap-2">
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            onClick={() => generateReport(report.id, "preview")}
                                            disabled={loading[report.id]}
                                            className="flex-1 border-slate-300 text-slate-700 hover:bg-slate-50"
                                            data-testid={`preview-${report.id}`}
                                        >
                                            {loading[report.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Eye className="mr-1" />}
                                        </Button>
                                        <Button
                                            size="sm"
                                            onClick={() => generateReport(report.id, "download")}
                                            disabled={loading[report.id]}
                                            className="flex-1 btn-primary"
                                            data-testid={`download-${report.id}`}
                                        >
                                            {loading[report.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        );
                    })}
                </div>
            ) : (
                <Card className="card-surface">
                    <CardContent className="p-4 space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                            <input
                                type="text"
                                placeholder="Search by risk id/category/description"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none md:col-span-2"
                            />
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                            />
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                            />
                            <select
                                value={riskStatus}
                                onChange={(e) => setRiskStatus(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                            >
                                <option value="">All Responses</option>
                                <option value="accept">Accept</option>
                                <option value="reduce">Reduce</option>
                                <option value="transfer">Transfer</option>
                                <option value="avoid">Avoid</option>
                            </select>
                            <select
                                value={riskSeverity}
                                onChange={(e) => setRiskSeverity(e.target.value)}
                                className="border border-gray-300 rounded-lg px-3 py-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                            >
                                <option value="">All Severity</option>
                                <option value="extreme">Extreme</option>
                                <option value="significant">Significant</option>
                                <option value="moderate">Moderate</option>
                                <option value="low">Low</option>
                            </select>
                        </div>

                        <div className="flex items-center gap-2">
                            <Button onClick={() => loadRiskHistory(1)} className="btn-primary">Get report</Button>
                            <Button variant="outline" onClick={clearFilters}>Clear</Button>
                        </div>

                        <DataTable
                            columns={historyColumns}
                            data={historyRows}
                            getRowId={(row) => String(row.id)}
                            loading={historyLoading}
                            emptyMessage="No risk history found"
                            manualPagination
                            rowCount={totalCount}
                            pagination={{ pageIndex: (currentPage || 1) - 1, pageSize: itemsPerPage }}
                            onPaginationChange={(updater) => {
                                const current = { pageIndex: (currentPage || 1) - 1, pageSize: itemsPerPage };
                                const next = typeof updater === "function" ? updater(current) : updater;
                                if (next.pageSize !== itemsPerPage) {
                                    setCurrentPage(1);
                                    setItemsPerPage(next.pageSize);
                                } else {
                                    loadRiskHistory(next.pageIndex + 1);
                                }
                            }}
                            enableColumnVisibility={false}
                            enableColumnResizing
                            maxBodyHeight="65vh"
                        />
                    </CardContent>
                </Card>
            )}

            <Dialog open={!!historyDetail} onOpenChange={() => setHistoryDetail(null)}>
                <DialogContent className="bg-white border-slate-200 max-w-2xl max-h-[80vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-slate-800">
                            {historyDetail?.risk_id} History
                        </DialogTitle>
                    </DialogHeader>
                    {historyDetail && (
                        <div className="overflow-y-auto max-h-[60vh] mt-2 space-y-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500">Risk Type</p>
                                    <p className="text-sm text-slate-800">{historyDetail.risk_type || "N/A"}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Sub Category</p>
                                    <p className="text-sm text-slate-800">{historyDetail.risk_sub_category || "N/A"}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Created By</p>
                                    <p className="text-sm text-slate-800">{historyDetail.created_by || "N/A"}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500">Updated By</p>
                                    <p className="text-sm text-slate-800">{historyDetail.updated_by || "N/A"}</p>
                                </div>
                            </div>

                            <div>
                                <p className="text-sm font-semibold text-slate-800 mb-2">History Timeline</p>
                                {historyDetail.history?.length ? (
                                    <div className="space-y-2 max-h-48 overflow-y-auto pr-2">
                                        {historyDetail.history.map((log, idx) => (
                                            <div key={`${historyDetail.id}-log-${idx}`} className="p-2 rounded border bg-white">
                                                <p className="text-sm text-slate-800">{log.description || "No description"}</p>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {log.log_type || "N/A"} by {log.logged_by || "system"} at {log.created_at ? new Date(log.created_at).toLocaleString() : "N/A"}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <p className="text-sm text-slate-500">No history in selected date range.</p>
                                )}
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>

            <Dialog open={!!previewData} onOpenChange={() => setPreviewData(null)}>
                <DialogContent className="bg-white border-slate-200 max-w-4xl max-h-[80vh] overflow-hidden" data-testid="report-preview-modal">
                    <DialogHeader>
                        <DialogTitle className="text-xl font-semibold text-slate-800">
                            {previewReport?.name} Preview
                        </DialogTitle>
                    </DialogHeader>
                    <div className="overflow-y-auto max-h-[60vh] mt-4">
                        {previewData?.data ? (
                            <div className="space-y-4">
                                <div className="text-xs text-slate-500">
                                    Generated: {new Date(previewData.generated_at).toLocaleString()}
                                </div>
                                {previewData.data.length === 0 ? (
                                    <p className="text-slate-500 text-center py-8">No data available</p>
                                ) : (
                                    <DataTable
                                        columns={[
                                            {
                                                accessorKey: "risk_id",
                                                header: "Risk ID",
                                                cell: ({ getValue }) => <span className="font-mono text-red-600">{getValue()}</span>,
                                            },
                                            {
                                                accessorKey: "risk_description",
                                                header: "Description",
                                                cell: ({ getValue }) => <span className="text-slate-700">{getValue()}</span>,
                                            },
                                            {
                                                accessorKey: "category",
                                                header: "Category",
                                                cell: ({ getValue }) => <span className="text-slate-600">{getValue()}</span>,
                                            },
                                            {
                                                accessorKey: "inherent_risk_score",
                                                header: "Inherent Rating",
                                                cell: ({ getValue }) => <span>{getValue() || "N/A"}</span>,
                                            },
                                            {
                                                accessorKey: "residual_risk_score",
                                                header: "Residual Rating",
                                                cell: ({ getValue }) => <span>{getValue() || "N/A"}</span>,
                                            },
                                        ]}
                                        data={previewData.data.slice(0, 20)}
                                        getRowId={(item) => String(item.id)}
                                        enableColumnVisibility={false}
                                    />
                                )}
                            </div>
                        ) : previewData?.stats ? (
                            <div className="space-y-6">
                                <div className="text-xs text-slate-500">
                                    Generated: {new Date(previewData.generated_at).toLocaleString()}
                                </div>
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                    <div className="p-4 rounded-lg bg-slate-50 border border-slate-200">
                                        <p className="text-2xl font-bold text-slate-800">{previewData.stats.total_risks}</p>
                                        <p className="text-xs text-slate-500">Total Risks</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-red-50 border border-red-200">
                                        <p className="text-2xl font-bold text-red-700">{previewData.stats.critical_high_risks}</p>
                                        <p className="text-xs text-red-600">Critical/High</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-amber-50 border border-amber-200">
                                        <p className="text-2xl font-bold text-amber-700">{previewData.stats.moderate_risks}</p>
                                        <p className="text-xs text-amber-600">Moderate</p>
                                    </div>
                                    <div className="p-4 rounded-lg bg-green-50 border border-green-200">
                                        <p className="text-2xl font-bold text-green-700">{previewData.stats.low_risks}</p>
                                        <p className="text-xs text-green-600">Low</p>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};
